import React, { useId, useRef } from "react";
import { GitBranch, Sparkle, Square, Upload } from "lucide-react";
import { SoonBadge, SOON_TOOLTIP } from "../../atoms/SoonBadge";
import { Tooltip } from "../../atoms/Tooltip";
import { ChoiceCard } from "../ChoiceCard";
import { Grid } from "./RecipePickerStep.styles";
import { METHOD_PICKER_COPY } from "./MethodPickerStep.copy";
import {
  FileCell,
  GoalField,
  GoalHelper,
  GoalInput,
  GoalLabel,
  Methods,
  TemplateChip,
  TemplateChips,
  Templates,
  TemplatesHeading,
} from "./MethodPickerStep.styles";
import type { MethodPickerStepProps } from "./RecipePickerStep.types";

const ICON_SIZE = 16;

/**
 * The new Story path's one step for picking how to start (F04): Start with AI and Blank canvas
 * work; From a file is drawn disabled with a Soon badge until the backend takes it. The template
 * chips are samples (marked) that fill the goal and seed the audience.
 * The audience is no longer asked here; it is edited on the Plan step, which a template's audience
 * seeds through the create request.
 *
 * A separate body from `RecipePickerStep`, which keeps the illustrated cards, templates and agents
 * of the old path. Both share the `{ values, onChange }` contract and the selection ids.
 */
export const MethodPickerStep: React.FC<MethodPickerStepProps> = ({
  values,
  onChange,
  context,
}) => {
  const { selectedRecipeId, goal = "" } = values;
  const goalId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileUpload = context?.fileUpload;

  return (
    <Methods>
      <Grid>
        <ChoiceCard
          title={METHOD_PICKER_COPY.ai.title}
          description={METHOD_PICKER_COPY.ai.description}
          icon={<Sparkle size={ICON_SIZE} strokeWidth={1.75} />}
          selected={selectedRecipeId === "ai"}
          onSelect={() => onChange({ selectedRecipeId: "ai" })}
        />
        <ChoiceCard
          title={METHOD_PICKER_COPY.blank.title}
          description={METHOD_PICKER_COPY.blank.description}
          icon={<Square size={ICON_SIZE} strokeWidth={1.75} />}
          iconTone="neutral"
          selected={selectedRecipeId === "blank-canvas"}
          onSelect={() => onChange({ selectedRecipeId: "blank-canvas" })}
        />
        <FileCell>
          {fileUpload ? (
            fileUpload.disabledReason ? (
              <Tooltip label={fileUpload.disabledReason} multiline>
              <ChoiceCard
                title={METHOD_PICKER_COPY.file.title}
                description={METHOD_PICKER_COPY.file.description}
                icon={<Upload size={ICON_SIZE} strokeWidth={1.75} />}
                iconTone="info"
                selected={false}
                onSelect={() => {
                  if (!fileUpload.disabledReason && !fileUpload.isBusy) fileInputRef.current?.click();
                }}
              />
              </Tooltip>
            ) : (
              <ChoiceCard
                title={METHOD_PICKER_COPY.file.title}
                description={METHOD_PICKER_COPY.file.description}
                icon={<Upload size={ICON_SIZE} strokeWidth={1.75} />}
                iconTone="info"
                selected={false}
                onSelect={() => {
                  if (!fileUpload.disabledReason && !fileUpload.isBusy) fileInputRef.current?.click();
                }}
              />
            )
          ) : (
            <Tooltip label={SOON_TOOLTIP}>
              <ChoiceCard
                title={METHOD_PICKER_COPY.file.title}
                description={METHOD_PICKER_COPY.file.description}
                icon={<Upload size={ICON_SIZE} strokeWidth={1.75} />}
                iconTone="info"
                selected={false}
                soon
                onSelect={() => undefined}
              />
            </Tooltip>
          )}
        </FileCell>
      </Grid>
      {fileUpload ? (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            hidden
            data-testid="method-file-input"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (file) fileUpload.onPick(file);
            }}
          />
          {fileUpload.error ? <GoalHelper role="alert">{fileUpload.error}</GoalHelper> : null}
        </>
      ) : null}
      {selectedRecipeId === "ai" ? (
        <GoalField>
          <GoalLabel htmlFor={goalId}>{METHOD_PICKER_COPY.goal.label}</GoalLabel>
          <GoalInput
            id={goalId}
            value={goal}
            placeholder={METHOD_PICKER_COPY.goal.placeholder}
            autoFocus
            onChange={(event) => onChange({ goal: event.target.value })}
          />
          <GoalHelper>
            <GitBranch size={14} strokeWidth={1.75} aria-hidden="true" />
            {METHOD_PICKER_COPY.goal.helper}
          </GoalHelper>
        </GoalField>
      ) : null}
      {/* No templates source exists yet: the section stays visible, disabled, with no sample cards. */}
      <Templates>
        <TemplatesHeading>
          {METHOD_PICKER_COPY.templatesHeading}
          <SoonBadge />
        </TemplatesHeading>
        <TemplateChips>
          <Tooltip label={SOON_TOOLTIP}>
            <TemplateChip type="button" aria-disabled="true" onClick={(event) => event.preventDefault()}>
              {METHOD_PICKER_COPY.templatesChip}
            </TemplateChip>
          </Tooltip>
        </TemplateChips>
      </Templates>
    </Methods>
  );
};

export default MethodPickerStep;
